import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * <recently-viewed-section>
 *
 * Reads the visitor's recently-viewed history from localStorage and renders
 * a carousel of product cards. Each entry is fetched directly via Shopify's
 * `/products/{handle}.js` endpoint — no search API, no section rendering,
 * nothing to negotiate with the CLI dev proxy.
 *
 * Behaviour rules (Patrick's requirements):
 *   1. Only show the section when there is at least one product to render.
 *   2. When the carousel is empty, the entire wrapper is hidden — heading,
 *      blocks and all.
 *   3. Re-render on pageshow / pagereveal / storage / recently-viewed:change
 *      so the carousel always reflects the current viewing history.
 */
class RecentlyViewedSection extends HTMLElement {
  /** @type {boolean} */
  #pendingHydrate = false;
  /** @type {boolean} */
  #hydrating = false;

  connectedCallback() {
    // Defer initial hydration to idle — keeps it off the critical path.
    this.#scheduleHydrate();

    window.addEventListener(RecentlyViewed.CHANGE_EVENT, this.#handleChange);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagereveal', this.#handlePageShow);
    window.addEventListener('storage', this.#handleStorage);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    window.removeEventListener(RecentlyViewed.CHANGE_EVENT, this.#handleChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagereveal', this.#handlePageShow);
    window.removeEventListener('storage', this.#handleStorage);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /** Public API — force-refresh the carousel. */
  refresh() {
    this.#hydrate();
  }

  #handleChange = () => this.#scheduleHydrate();
  #handlePageShow = () => this.#scheduleHydrate();
  #handleStorage = (/** @type {StorageEvent} */ event) => {
    if (event.key !== 'viewedProducts' && event.key !== null) return;
    this.#scheduleHydrate();
  };
  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#scheduleHydrate();
  };

  #scheduleHydrate() {
    if (this.#pendingHydrate) return;
    this.#pendingHydrate = true;
    const schedule =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 200);
    schedule(() => {
      this.#pendingHydrate = false;
      this.#hydrate();
    });
  }

  async #hydrate() {
    if (this.#hydrating) {
      // A new run is requested while one is still pending — schedule a
      // follow-up so the final state always reflects the latest list.
      this.#scheduleHydrate();
      return;
    }
    this.#hydrating = true;

    try {
      const entries = RecentlyViewed.getEntries();
      const currentProductId = this.dataset.currentProductId
        ? String(this.dataset.currentProductId)
        : null;

      // Drop the current PDP product so we don't show "what you're already
      // looking at" inside the recently-viewed carousel.
      const candidates = entries.filter((entry) => entry.id !== currentProductId);

      console.log(
        '[recently-viewed] candidates after filter:',
        candidates.length,
        candidates.map((c) => c.id)
      );

      if (candidates.length === 0) {
        this.#hideSection('no candidates');
        return;
      }

      // Fetch product JSON in parallel for every candidate that has a
      // handle. Legacy entries (handle === null) get filtered out — they
      // were added before we started persisting the handle and we can't
      // resolve them without one.
      const fetchable = candidates.filter((c) => !!c.handle);
      const legacyCount = candidates.length - fetchable.length;
      if (legacyCount > 0) {
        console.log(
          '[recently-viewed] dropping',
          legacyCount,
          'legacy entries without handle'
        );
      }

      if (fetchable.length === 0) {
        this.#hideSection('no fetchable handles');
        return;
      }

      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const results = await Promise.all(
        fetchable.map(async (entry) => {
          try {
            const res = await fetch(`${root}products/${encodeURIComponent(entry.handle)}.js`);
            if (!res.ok) return null;
            return await res.json();
          } catch (_error) {
            return null;
          }
        })
      );

      const products = results.filter(Boolean);
      console.log(
        '[recently-viewed] fetched',
        products.length,
        'products from',
        fetchable.length,
        'handles'
      );

      if (products.length === 0) {
        this.#hideSection('no products fetched');
        return;
      }

      // Render the carousel with our simple, dependency-free card markup.
      this.#renderCards(products);
      this.#showSection(products.length);
    } finally {
      this.#hydrating = false;
    }
  }

  /**
   * @param {any[]} products - Array of product objects from /products/{handle}.js
   */
  #renderCards(products) {
    // Look for the track that the original section rendered. If none, fall
    // back to a freshly-injected one — section may have been server-rendered
    // empty when no fallback was configured.
    let track = this.querySelector('[data-recently-viewed-track]');
    if (!track) {
      const host = this.querySelector('.section-resource-list__content')
        ?.parentElement || this;
      track = document.createElement('div');
      track.setAttribute('data-recently-viewed-track', '');
      track.classList.add('recently-viewed-section__track');
      host.appendChild(track);
    }

    track.innerHTML = '';
    track.classList.add('recently-viewed-section__track');
    track.removeAttribute('data-has-products');
    track.setAttribute('data-has-products', 'true');

    const fragment = document.createDocumentFragment();
    products.forEach((product) => {
      const card = this.#createCard(product);
      if (card) fragment.appendChild(card);
    });
    track.appendChild(fragment);
  }

  /**
   * Build a product card that visually matches the theme's regular
   * `resource-card` (used by product-recommendations, related products,
   * etc). Includes hover image, wishlist heart button, and the same
   * link/title/price layout — so the carousel slots in next to other
   * carousels on the page without looking out of place.
   *
   * @param {any} product - product object from /products/{handle}.js
   * @returns {HTMLElement | null}
   */
  #createCard(product) {
    if (!product || !product.handle) return null;

    const variant =
      (Array.isArray(product.variants) && product.variants.find((v) => v.available)) ||
      (Array.isArray(product.variants) && product.variants[0]) ||
      null;

    const url = variant ? `${product.url}?variant=${variant.id}` : product.url;

    const primaryImage =
      (variant && variant.featured_image && variant.featured_image.src) ||
      product.featured_image ||
      (Array.isArray(product.images) && product.images[0]) ||
      '';
    const hoverImage =
      (Array.isArray(product.images) && product.images.length > 1 && product.images[1]) || '';

    const ratio =
      (variant && variant.featured_image && variant.featured_image.aspect_ratio) ||
      (product.featured_image && product.featured_image.aspect_ratio) ||
      0.8;

    const price = this.#formatMoney(variant ? variant.price : product.price);
    const compareAt = this.#formatMoney(variant ? variant.compare_at_price : product.compare_at_price);
    const onSale = variant
      ? variant.compare_at_price && variant.compare_at_price > variant.price
      : false;

    const wrapper = document.createElement('div');
    wrapper.className = 'resource-card recently-viewed-card';
    wrapper.dataset.productId = String(product.id);
    wrapper.dataset.resourceType = 'product';

    const titleEsc = this.#escape(product.title);
    const variantId = variant ? variant.id : '';

    // No per-card image slideshow: the cards live inside a horizontal
    // scroll-snap carousel themselves, so swiping inside a card would
    // fight the parent's scroll. We stick to primary image + hover-image
    // fade (desktop only) which gives a "second view" hint without
    // any swipe conflict.
    wrapper.innerHTML = `
      ${this.#wishlistButtonHTML(product, variantId)}
      <a class="resource-card__link" href="${this.#escape(url)}">
        <span class="visually-hidden">${titleEsc}</span>
      </a>
      <div
        class="resource-card__media recently-viewed-card__media"
        style="--resource-card-aspect-ratio: ${ratio};"
      >
        ${primaryImage
          ? `<img class="resource-card__image recently-viewed-card__image recently-viewed-card__image--primary"
                  src="${this.#sized(primaryImage, 480)}"
                  srcset="${this.#sized(primaryImage, 240)} 240w, ${this.#sized(primaryImage, 480)} 480w, ${this.#sized(primaryImage, 832)} 832w"
                  sizes="(min-width: 750px) 25vw, 50vw"
                  alt="${titleEsc}"
                  loading="lazy"
                  width="480" height="${Math.round(480 / ratio)}">`
          : '<span class="recently-viewed-card__image recently-viewed-card__image--placeholder" aria-hidden="true"></span>'}
        ${hoverImage
          ? `<img class="resource-card__image recently-viewed-card__image recently-viewed-card__image--hover"
                  src="${this.#sized(hoverImage, 480)}"
                  srcset="${this.#sized(hoverImage, 240)} 240w, ${this.#sized(hoverImage, 480)} 480w, ${this.#sized(hoverImage, 832)} 832w"
                  sizes="(min-width: 750px) 25vw, 50vw"
                  alt=""
                  loading="lazy"
                  aria-hidden="true"
                  width="480" height="${Math.round(480 / ratio)}">`
          : ''}
      </div>
      <div class="recently-viewed-card__content">
        <div class="spacing-style text-block h5 text-block--align-left recently-viewed-card__title">
          <p>${titleEsc}</p>
        </div>
        <div class="recently-viewed-card__price" ref="priceContainer">
          ${onSale ? `<s class="price price--compare">${compareAt}</s>&nbsp;` : ''}
          <span class="price${onSale ? ' price--on-sale' : ''}">${price}</span>
        </div>
      </div>
    `;
    return wrapper;
  }

  /**
   * Renders the same `<wishlist-button>` custom-element markup that the
   * snippet produces, so the button auto-upgrades and starts toggling the
   * heart state once it's connected to the DOM.
   *
   * @param {any} product
   * @param {string | number} variantId
   * @returns {string}
   */
  #wishlistButtonHTML(product, variantId) {
    const labels = this.#wishlistLabels();
    const handleAttr = this.#escape(product.handle || '');
    const idAttr = String(product.id);
    const variantAttr = variantId ? `data-variant-id="${this.#escape(String(variantId))}"` : '';

    return `
      <wishlist-button
        class="wishlist-button-wrapper wishlist-button-wrapper--card"
        data-product-id="${idAttr}"
        data-product-handle="${handleAttr}"
        ${variantAttr}
        data-label-add="${this.#escape(labels.add)}"
        data-label-remove="${this.#escape(labels.remove)}"
      >
        <button
          type="button"
          class="button button-unstyled wishlist-button wishlist-button--card"
          aria-pressed="false"
          aria-label="${this.#escape(labels.add)}"
          title="${this.#escape(labels.add)}"
        >
          <span class="iconify i-custom:heart shrink-0 size-6 wishlist-button__icon wishlist-button__icon--outline" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </span>
          <span class="iconify i-custom:heart shrink-0 size-6 wishlist-button__icon wishlist-button__icon--filled" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </span>
          <span class="visually-hidden wishlist-button__text">${this.#escape(labels.add)}</span>
        </button>
      </wishlist-button>
    `;
  }

  /**
   * Reads the merchant-localised "Add to wishlist" / "Remove from wishlist"
   * labels off the section element (set as data-* attributes from Liquid).
   * Falls back to English strings if the section didn't provide them.
   */
  #wishlistLabels() {
    return {
      add: this.dataset.labelWishlistAdd || 'Add to wishlist',
      remove: this.dataset.labelWishlistRemove || 'Remove from wishlist',
    };
  }

  #showSection(count) {
    this.removeAttribute('hidden');
    this.removeAttribute('data-initially-empty');
    this.setAttribute('data-has-viewed', '');
    console.log('[recently-viewed] rendered', count, 'product card(s)');
  }

  #hideSection(reason) {
    this.setAttribute('hidden', '');
    this.removeAttribute('data-has-viewed');
    console.log('[recently-viewed] hiding section —', reason);
  }

  #formatMoney(cents) {
    if (cents == null) return '';
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    const symbolByCurrency = { EUR: '€', USD: '$', GBP: '£' };
    const symbol = symbolByCurrency[currency] || currency;
    const locale = document.documentElement.lang || 'nl-NL';

    // Format the NUMBER only, then prepend the symbol directly (no
    // separating space). This matches the theme's Liquid `money` filter
    // output — `€19,99` instead of the `Intl.NumberFormat`-flavoured
    // `€ 19,99` with a non-breaking space.
    try {
      const number = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
      return `${symbol}${number}`;
    } catch (_error) {
      return `${symbol}${(cents / 100).toFixed(2)}`;
    }
  }

  /**
   * @param {string} src
   * @param {number} width
   */
  #sized(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return url.replace(/(\.(jpg|jpeg|png|webp|gif))(\?.*)?$/i, `_${width}x$1$3`);
  }

  /** @param {string} value */
  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        default: return '&#39;';
      }
    });
  }
}

if (!customElements.get('recently-viewed-section')) {
  customElements.define('recently-viewed-section', RecentlyViewedSection);
}

// Debug helper — `recentlyViewedDebug()` in console shows state of every
// section instance and forces a fresh fetch.
if (typeof window !== 'undefined') {
  window.recentlyViewedDebug = function recentlyViewedDebug() {
    const entries = RecentlyViewed.getEntries();
    const sections = document.querySelectorAll('recently-viewed-section');
    console.group('[recently-viewed] DEBUG');
    console.log('localStorage entries:', entries);
    console.log('Found %d recently-viewed-section instance(s)', sections.length);
    sections.forEach((el, i) => {
      console.log(`  #${i}`, {
        currentProductId: el.dataset.currentProductId,
        hidden: el.hasAttribute('hidden'),
        hasViewed: el.hasAttribute('data-has-viewed'),
      });
      if (typeof el.refresh === 'function') el.refresh();
    });
    console.groupEnd();
    return { entries, sections: sections.length };
  };
}
